// ---------------------------------------------------------------------------
// C49: Conversational Form Mode — one question at a time, chat-style
// ---------------------------------------------------------------------------
import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Send, Check } from "lucide-react";
import { evaluateCondition, type FormField } from "@/hooks/use-forms";

const BUSINESS_SIGNAL_RE = /\b(business|company|enterprise|agency|team|b2b|organization)\b/i;
const CONSUMER_SIGNAL_RE = /\b(consumer|personal|individual|myself|family|home)\b/i;
const BUDGET_SIGNAL_RE = /\b(budget|pricing|price|revenue|spend|cost)\b/i;
const TIMELINE_SIGNAL_RE = /\b(urgent|asap|today|this week|timeline|deadline|when)\b/i;
const DETAIL_SIGNAL_RE = /\b(help|need|problem|issue|goal|project|details?)\b/i;

function rankConversationalFields(
  fields: FormField[],
  answers: Record<string, string>,
): FormField[] {
  const answerText = Object.values(answers).join(" ").trim();

  return fields
    .map((field, index) => {
      const label = field.label.toLowerCase();
      const type = field.type.toLowerCase();
      let score = 100 - index;

      if (!answerText) {
        if (type === "radio" || type === "dropdown" || type === "select") score += 24;
        if (type === "email") score += 18;
        if (/name/.test(label) && !/company/.test(label)) score += 16;
        if (field.required) score += 8;
        return { field, score, index };
      }

      if (type === "email") score += 18;
      if (/name/.test(label) && !/company/.test(label)) score += 14;
      if (field.required) score += 8;

      if (BUSINESS_SIGNAL_RE.test(answerText)) {
        if (/company|business|organization/.test(label)) score += 40;
        if (/role|title|team/.test(label)) score += 28;
        if (BUDGET_SIGNAL_RE.test(label)) score += 22;
      }

      if (CONSUMER_SIGNAL_RE.test(answerText) && /company|business|organization/.test(label)) {
        score -= 25;
      }

      if (BUDGET_SIGNAL_RE.test(answerText) && BUDGET_SIGNAL_RE.test(label)) {
        score += 30;
      }

      if (TIMELINE_SIGNAL_RE.test(answerText) && (/date|when|timeline/.test(label) || type === "date")) {
        score += 26;
      }

      if (DETAIL_SIGNAL_RE.test(answerText) && (type === "textarea" || /details?|message|goal|project/.test(label))) {
        score += 24;
      }

      return { field, score, index };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((entry) => entry.field);
}

interface ConversationalFormProps {
  fields: FormField[];
  formName: string;
  onSubmit: (answers: Record<string, string>) => void;
}

export function ConversationalForm({ fields, formName, onSubmit }: ConversationalFormProps) {
  // Filter out layout fields (heading, paragraph, divider)
  const inputFields = fields.filter((f) => !["heading", "paragraph", "divider", "hidden"].includes(f.type));

  const [questionPath, setQuestionPath] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const visibleFields = inputFields.filter((field) => evaluateCondition(field, answers));
  const visibleFieldMap = new Map(visibleFields.map((field) => [field.id, field]));
  const currentFieldId = questionPath[currentIndex] ?? null;
  const currentField = currentFieldId ? visibleFieldMap.get(currentFieldId) ?? null : null;
  const progress = visibleFields.length > 0 ? ((currentIndex + 1) / visibleFields.length) * 100 : 0;

  function chooseNextFieldId(
    nextAnswers: Record<string, string>,
    askedFieldIds: string[],
  ): string | null {
    const nextVisibleFields = inputFields.filter((field) => evaluateCondition(field, nextAnswers));
    const remainingFields = nextVisibleFields.filter((field) => !askedFieldIds.includes(field.id));
    if (remainingFields.length === 0) {
      return null;
    }

    const ranked = rankConversationalFields(remainingFields, nextAnswers);
    return ranked[0]?.id ?? null;
  }

  useEffect(() => {
    const nextVisibleIds = new Set(visibleFields.map((field) => field.id));
    setQuestionPath((prev) => {
      const pruned = prev.filter((fieldId) => nextVisibleIds.has(fieldId));
      if (pruned.length > 0) {
        return pruned;
      }

      const firstFieldId = chooseNextFieldId(answers, []);
      return firstFieldId ? [firstFieldId] : [];
    });
  }, [answers, fields]);

  useEffect(() => {
    if (questionPath.length === 0 || visibleFields.length === 0) {
      setCurrentIndex(0);
      return;
    }

    if (currentIndex >= questionPath.length) {
      setCurrentIndex(questionPath.length - 1);
    }
  }, [currentIndex, questionPath.length, visibleFields.length]);

  // Typing animation on question change
  useEffect(() => {
    if (!currentField) return;
    setIsTyping(true);
    const timer = setTimeout(() => {
      setIsTyping(false);
      inputRef.current?.focus();
    }, 600);
    return () => clearTimeout(timer);
  }, [currentFieldId]);

  // Pre-fill answer if going back
  useEffect(() => {
    if (currentField) {
      setCurrentAnswer(answers[currentField.id] ?? "");
    }
  }, [currentIndex, currentField, answers]);

  const handleNext = (answerOverride?: string) => {
    if (!currentField) return;
    const answerToPersist = answerOverride ?? currentAnswer;

    // Validate required
    if (currentField.required && !answerToPersist.trim()) return;

    // Save answer
    const updated = { ...answers, [currentField.id]: answerToPersist };
    setAnswers(updated);

    const nextFieldId = chooseNextFieldId(updated, questionPath);
    if (nextFieldId) {
      const nextPath = [...questionPath, nextFieldId];
      setQuestionPath(nextPath);
      setCurrentIndex(nextPath.length - 1);
      setCurrentAnswer("");
    } else {
      // Complete
      setIsComplete(true);
      onSubmit(updated);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  if (isComplete) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-16 w-16 rounded-full bg-[var(--color-success)]/10 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-[var(--color-success)]" />
        </div>
        <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Thank you!</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">Your response has been submitted.</p>
      </div>
    );
  }

  if (!currentField) return null;

  return (
    <div className="max-w-lg mx-auto py-12 px-4">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[var(--color-muted-foreground)]">{formName}</span>
          <span className="text-xs text-[var(--color-muted-foreground)] tabular-nums">
            {currentIndex + 1} of {visibleFields.length}
          </span>
        </div>
        <div className="h-1 rounded-full bg-[var(--color-muted)] overflow-hidden">
          <div
            className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        {isTyping ? (
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[var(--color-primary)] animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="h-2 w-2 rounded-full bg-[var(--color-primary)] animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="h-2 w-2 rounded-full bg-[var(--color-primary)] animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        ) : (
          <h2 className="text-2xl font-semibold text-[var(--color-foreground)] animate-in fade-in slide-in-from-bottom-2 duration-300">
            {currentField.label}
            {currentField.required && <span className="text-[var(--color-destructive)] ml-1">*</span>}
          </h2>
        )}
      </div>

      {/* Answer input */}
      {!isTyping && (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: "100ms" }}>
          {currentField.type === "textarea" ? (
            <textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={currentField.placeholder ?? "Type your answer..."}
              rows={3}
              className="w-full border-b-2 border-[var(--color-border)] bg-transparent text-lg text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]/50 focus:outline-none focus:border-[var(--color-primary)] transition-colors resize-none"
              autoFocus
            />
          ) : currentField.type === "dropdown" || currentField.type === "select" || currentField.type === "radio" ? (
            <div className="space-y-2">
              {(currentField.options ?? []).map((opt, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setCurrentAnswer(opt);
                    setTimeout(() => handleNext(opt), 200);
                  }}
                  className={`w-full text-left rounded-lg border px-4 py-3 text-sm transition-all ${
                    currentAnswer === opt
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]"
                      : "border-[var(--color-border)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/30"
                  }`}
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-current mr-2 text-xs">
                    {String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                </button>
              ))}
            </div>
          ) : currentField.type === "checkbox" ? (
            <div className="space-y-2">
              {(currentField.options ?? []).map((opt, i) => {
                const selected = currentAnswer.split(",").map((s) => s.trim()).includes(opt);
                return (
                  <button
                    key={i}
                    onClick={() => {
                      const current = currentAnswer.split(",").map((s) => s.trim()).filter(Boolean);
                      const next = selected ? current.filter((c) => c !== opt) : [...current, opt];
                      setCurrentAnswer(next.join(", "));
                    }}
                    className={`w-full text-left rounded-lg border px-4 py-3 text-sm transition-all ${
                      selected ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-[var(--color-border)] hover:border-[var(--color-primary)]/30"
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded border mr-2 text-xs ${selected ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]" : "border-current"}`}>
                      {selected ? "✓" : ""}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
          ) : (
            <input
              ref={inputRef}
              type={currentField.type === "email" ? "email" : currentField.type === "phone" ? "tel" : currentField.type === "number" ? "number" : currentField.type === "date" ? "date" : "text"}
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={currentField.placeholder ?? "Type your answer..."}
              className="w-full border-b-2 border-[var(--color-border)] bg-transparent text-lg text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]/50 focus:outline-none focus:border-[var(--color-primary)] transition-colors py-2"
              autoFocus
            />
          )}
        </div>
      )}

      {/* Navigation */}
      {!isTyping && (
        <div className="flex items-center justify-between mt-8 animate-in fade-in duration-300" style={{ animationDelay: "200ms" }}>
          <button
            onClick={handleBack}
            disabled={currentIndex === 0}
            className="flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <button
            onClick={() => handleNext()}
            className="btn-press flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white"
            style={{ background: "var(--gradient-energy)" }}
          >
            {currentIndex === visibleFields.length - 1 ? (
              <><Send className="h-4 w-4" /> Submit</>
            ) : (
              <>Next <ChevronRight className="h-4 w-4" /></>
            )}
          </button>
        </div>
      )}

      {/* Keyboard hint */}
      {!isTyping && (
        <p className="mt-4 text-center text-[10px] text-[var(--color-muted-foreground)]">
          Press <kbd className="rounded border border-[var(--color-border)] px-1">Enter ↵</kbd> to continue
        </p>
      )}
    </div>
  );
}
